import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { api } from "../services/AxiosService";
import getBITSEndApiMap from "../apiMap";

// Types
export interface CourseStats {
  totalAssignments: number;
  totalProjects: number;
  completedProjects: number;
  inProgress: number;
  notStarted: number;
  completionPercentage: number;
}

export interface ProjectProgress {
  percentage: number;
  status: "not_started" | "in_progress" | "completed";
  lastActivity: string | null;
}

export interface CourseProject {
  id: string;
  title: string;
  status: string;
  difficultyLevel: string;
  startDate: string | null;
  dueDate: string | null;
  totalPoints: number;
  progress: ProjectProgress;
  hasAssignment: boolean;
}

export interface Platform {
  id: string;
  name: string;
  url: string;
}

export interface Course {
  id: string;
  contextId: string;
  title: string;
  label: string;
  enrollmentStatus: string;
  roleInCourse: string;
  enrolledAt: string;
  platform: Platform | null;
  stats: CourseStats;
  projects: CourseProject[];
}

export interface CourseContent {
  id: number;
  name: string;
  summary?: string;
  visible: number;
  modules: CourseModule[];
}

export interface CourseModule {
  id: number;
  name: string;
  modname: string;
  visible: number;
  url?: string;
  description?: string;
  hasDetails?: boolean;
  linkedProject?: LinkedProject;
  lineItem?: LineItem;
  contents?: ModuleContent[];
  completed?: boolean;
  /** Whether this activity has completion tracking enabled in Moodle */
  completionTracked?: boolean;
  /**
   * How completion is tracked for this activity:
   * 'manual'    → student toggles it in Moodle (a "Mark as Complete" button applies),
   * 'automatic' → Moodle decides from conditions (e.g. "must view"); a manual
   *               button can't force it, it only triggers a view event,
   * 'local'     → Moodle doesn't track it, so the portal owns completion in its
   *               own DB (the button always works and sticks),
   * null        → not tracked anywhere.
   */
  completionTracking?: "manual" | "automatic" | "local" | null;
  quizStatus?: "submitted" | "in_progress" | "not_started" | "unknown";
}

export interface LinkedProject {
  id: string;
  title: string;
  description?: string;
  status: string;
  difficultyLevel: string;
  totalPoints: number;
  dueDate: string | null;
  progress: ProjectProgress;
}

export interface LineItem {
  id: string;
  label: string;
  scoreMaximum: number;
}

export interface ModuleContent {
  filename: string;
  fileurl: string | null;
  filesize: number;
  mimetype: string;
  type: string;
}

export interface CourseDetailsResponse {
  courseTitle: string;
  contextId: string;
  sections: CourseContent[];
  totalAssignments: number;
  linkedProjects: LinkedProject[];
}

export interface Assignment {
  id: number;
  courseId: number;
  name: string;
  intro: string;
  dueDate: Date | null;
  cutoffDate: Date | null;
  allowSubmissionsFromDate: Date | null;
  grade: number;
  maxGrade: number;
  timeModified: Date | null;
  linkedProject: LinkedProject | null;
}

export interface CourseAssignmentsResponse {
  courseTitle: string;
  contextId: string;
  assignments: Assignment[];
}

export interface CourseOverview {
  courseTitle: string;
  contextId: string;
  stats: {
    totalSections: number;
    totalModules: number;
    totalAssignments: number;
    totalProjects: number;
    completedProjects: number;
    inProgressProjects: number;
  };
}

export interface ModuleDetails {
  id?: number;
  courseModuleId?: number;
  instance?: number; // Moodle module instance ID (used for assignment status lookups)
  name?: string;
  intro?: string;
  activityInstructions?: string; // Moodle "Activity instructions" field (assign)
  description?: string;
  moduleType: string;
  // Quiz-specific fields
  timeLimit?: number;
  attempts?: number;
  attemptsList?: any[];
  grade?: number;
  timeOpen?: string | null;
  timeClose?: string | null;
  hasQuestions?: boolean;
  questionsPerPage?: number;
  shuffleAnswers?: boolean;
  // Lesson-specific fields
  maxPages?: number;
  practice?: boolean;
  // Forum-specific fields
  type?: string;
  assessed?: boolean;
  // LTI-specific fields
  linkedProject?: LinkedProject;
  // Page-specific fields
  content?: string;
  contentformat?: number;
  revision?: number;
  timemodified?: number;
  // Book-specific fields
  chapters?: Array<{
    id: number;
    title: string;
    content: string;
    pagenum: number;
    level?: number;
    hidden?: boolean;
  }>;
}

export interface AssignmentStatus {
  submissionStatus: "new" | "submitted" | "reopened";
  submittedAt: string | null;
  lastModified: string | null;
  graded: boolean;
  grade: number | null;
  maxGrade: number | null;
  gradeForDisplay: string | null;
  gradedDate: string | null;
  feedbackComments: string | null;
  locked: boolean;
  submittedFiles: {
    filename: string;
    filesize: number;
    fileurl: string;
    mimetype: string | null;
    timemodified: string | null;
  }[];
}

// API Functions
const fetchStudentCourses = async (token: string | null): Promise<Course[]> => {
  if (!token) {
    throw new Error("No authentication token available");
  }

  const url = getBITSEndApiMap("getStudentCourses");
  const response = await api.get(url);

  return response.data.data.courses;
};

const fetchCourseDetails = async (
  contextId: string,
  token: string | null,
): Promise<CourseDetailsResponse> => {
  if (!token) {
    throw new Error("No authentication token available");
  }

  const url = getBITSEndApiMap("getCourseDetails").replace(
    ":contextId",
    contextId,
  );
  const response = await api.get(url);

  return response.data.data;
};

const fetchCourseAssignments = async (
  contextId: string,
  token: string | null,
): Promise<CourseAssignmentsResponse> => {
  if (!token) {
    throw new Error("No authentication token available");
  }

  const url = getBITSEndApiMap("getCourseAssignments").replace(
    ":contextId",
    contextId,
  );
  const response = await api.get(url);

  return response.data.data;
};

const fetchCourseOverview = async (
  contextId: string,
  token: string | null,
): Promise<CourseOverview> => {
  if (!token) {
    throw new Error("No authentication token available");
  }

  const url = getBITSEndApiMap("getCourseOverview").replace(
    ":contextId",
    contextId,
  );
  const response = await api.get(url);

  return response.data.data;
};

const syncCourses = async (token: string | null): Promise<Course[]> => {
  if (!token) {
    throw new Error("No authentication token available");
  }

  const url = getBITSEndApiMap("syncStudentCourses");
  const response = await api.post(url, {});

  return response.data.data.courses;
};

// React Query Hooks
export const useStudentCoursesQuery = (token: string | null, options = {}) => {
  return useQuery({
    queryKey: ["studentCourses", token],
    queryFn: () => fetchStudentCourses(token),
    enabled: !!token,
    staleTime: 5 * 60 * 1000, // 5 minutes
    ...options,
  });
};

export const useCourseDetailsQuery = (
  contextId: string | undefined,
  token: string | null,
  options = {},
) => {
  return useQuery({
    queryKey: ["courseDetails", contextId, token],
    queryFn: () => fetchCourseDetails(contextId!, token),
    enabled: !!contextId && !!token,
    staleTime: 5 * 60 * 1000, // 5 minutes
    ...options,
  });
};

export const useCourseAssignmentsQuery = (
  contextId: string | undefined,
  token: string | null,
  options = {},
) => {
  return useQuery({
    queryKey: ["courseAssignments", contextId, token],
    queryFn: () => fetchCourseAssignments(contextId!, token),
    enabled: !!contextId && !!token,
    staleTime: 5 * 60 * 1000, // 5 minutes
    ...options,
  });
};

export const useCourseOverviewQuery = (
  contextId: string | undefined,
  token: string | null,
  options = {},
) => {
  return useQuery({
    queryKey: ["courseOverview", contextId, token],
    queryFn: () => fetchCourseOverview(contextId!, token),
    enabled: !!contextId && !!token,
    staleTime: 5 * 60 * 1000, // 5 minutes
    ...options,
  });
};

export const useSyncCoursesMutation = (token: string | null) => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: () => syncCourses(token),
    onSuccess: (data) => {
      // Invalidate and refetch courses query
      queryClient.setQueryData(["studentCourses", token], data);
      queryClient.invalidateQueries({ queryKey: ["studentCourses"] });
    },
  });
};

/**
 * Fetch module details from Moodle
 */
export const fetchModuleDetails = async (
  token: string | null,
  contextId: string,
  moduleId: string,
  modname: string,
): Promise<ModuleDetails> => {
  if (!token) {
    throw new Error("Authentication token is required");
  }

  const url = getBITSEndApiMap("getModuleDetails")
    .replace(":contextId", contextId)
    .replace(":moduleId", moduleId);

  const response = await api.get(url, {
    params: { modname },
  });

  return response.data.data;
};

/**
 * React Query hook for fetching module details
 */
export const useModuleDetailsQuery = (
  contextId: string,
  moduleId: string,
  modname: string,
  token: string | null,
  options?: any,
) => {
  const queryEnabled = !!contextId && !!moduleId && !!modname && !!token;

  return useQuery<ModuleDetails, Error>({
    queryKey: ["moduleDetails", contextId, moduleId, modname, token],
    queryFn: () => fetchModuleDetails(token, contextId, moduleId, modname),
    enabled: queryEnabled,
    staleTime: 5 * 60 * 1000, // 5 minutes
    ...options,
  });
};

/**
 * Fetch module completion status from Moodle
 */
export const useModuleCompletionQuery = (
  contextId: string,
  moduleId: string,
  token: string | null,
) => {
  return useQuery({
    queryKey: ["moduleCompletion", contextId, moduleId],
    queryFn: async () => {
      const url = getBITSEndApiMap("getModuleCompletionStatus")
        .replace(":contextId", contextId)
        .replace(":moduleId", moduleId);
      const response = await api.get(url);
      return response.data.data as {
        moduleId: string;
        completed: boolean;
        state: number;
        // 0 = none, 1 = manual, 2 = automatic (Moodle-driven)
        tracking: number | null;
      };
    },
    enabled: !!contextId && !!moduleId && !!token,
    staleTime: 0, // always fresh
  });
};

/**
 * Fetch course skills (custom field) from Moodle
 */
export const useCourseSkillsQuery = (
  contextId: string | undefined,
  token: string | null,
) => {
  return useQuery({
    queryKey: ["courseSkills", contextId],
    queryFn: async () => {
      const url = getBITSEndApiMap("getCourseSkills").replace(
        ":contextId",
        contextId!,
      );
      const response = await api.get(url);
      return response.data.data?.skills as string | null;
    },
    enabled: !!contextId && !!token,
    staleTime: 5 * 60 * 1000,
  });
};

/**
 * Mark a module as complete in Moodle
 */
export const useMarkModuleCompleteMutation = (contextId: string) => {
  const queryClient = useQueryClient();

  // Patch every cached courseDetails entry for this context (the query key also
  // includes the auth token, so match by contextId prefix) so the sidebar
  // checkmark and computed progress bar reflect the new completion state.
  const patchCourseDetailsCompletion = (moduleId: string, completed: boolean) => {
    const courseQueries = queryClient.getQueriesData<CourseDetailsResponse>({
      queryKey: ["courseDetails"],
    });
    courseQueries.forEach(([key, data]) => {
      if (!Array.isArray(key) || String(key[1]) !== String(contextId)) return;
      if (!data?.sections) return;
      const next: CourseDetailsResponse = {
        ...data,
        sections: data.sections.map((section) => ({
          ...section,
          modules: section.modules.map((mod) =>
            String(mod.id) === String(moduleId)
              ? { ...mod, completed, completionTracked: true }
              : mod,
          ),
        })),
      };
      queryClient.setQueryData(key, next);
    });
  };

  return useMutation({
    mutationFn: async ({
      moduleId,
      completed = true,
    }: {
      moduleId: string;
      completed?: boolean;
    }) => {
      const url = getBITSEndApiMap("markModuleComplete")
        .replace(":contextId", contextId)
        .replace(":moduleId", moduleId);
      const response = await api.post(url, { completed });
      return response.data.data as {
        moduleId: string;
        completed: boolean;
        manualCompletionSupported?: boolean;
        completionTracking?: "manual" | "automatic" | "local" | "none" | null;
        viewTriggered?: boolean;
        /**
         * A view event was sent but Moodle still reports the activity as
         * incomplete — its completion is gated on a condition a view can't
         * satisfy (e.g. a grade) or completion isn't actually enabled.
         */
        requiresViewCondition?: boolean;
      };
    },
    // Optimistically flip the UI so the button, sidebar highlight and progress
    // bar update instantly instead of flickering and reverting.
    onMutate: async ({ moduleId, completed = true }) => {
      const completionKey = ["moduleCompletion", contextId, moduleId];
      await queryClient.cancelQueries({ queryKey: completionKey });

      const prevCompletion = queryClient.getQueryData(completionKey);
      const prevCourseDetails = queryClient.getQueriesData({
        queryKey: ["courseDetails"],
      });

      queryClient.setQueryData(completionKey, {
        moduleId,
        completed,
        state: completed ? 1 : 0,
      });
      patchCourseDetailsCompletion(moduleId, completed);

      return { prevCompletion, prevCourseDetails, completionKey };
    },
    onError: (_error, _variables, context) => {
      // Roll back the optimistic update on failure.
      if (context?.prevCompletion !== undefined) {
        queryClient.setQueryData(context.completionKey, context.prevCompletion);
      }
      context?.prevCourseDetails?.forEach(([key, data]) => {
        queryClient.setQueryData(key, data);
      });
    },
    onSuccess: (data, { moduleId }) => {
      // Reconcile with the server's real completion state. The backend records
      // completion against the student's own account (manual completion, or a
      // view event for automatic-on-view activities) and reads it back, so
      // `data.completed` reflects what actually persisted — honor it rather than
      // optimistically forcing a green tick that would revert on refresh.
      const completed = data?.completed ?? true;

      queryClient.setQueryData(["moduleCompletion", contextId, moduleId], {
        moduleId,
        completed,
        state: completed ? 1 : 0,
      });
      patchCourseDetailsCompletion(moduleId, completed);

      // Explain, rather than silently revert, when the tick didn't stick. This
      // happens when the activity uses Moodle's automatic completion with a
      // condition a "mark complete" click can't satisfy (e.g. a required grade),
      // or completion isn't actually enabled for the activity.
      if (!completed && data?.requiresViewCondition) {
        toast.info(
          "This activity is completed automatically by Moodle once its requirements are met (for example, achieving a grade) — it can't be marked complete manually.",
        );
      }

      // Refresh overview counts (safe: does not drive the per-module tick).
      queryClient.invalidateQueries({
        queryKey: ["courseOverview"],
        exact: false,
      });

      // Refresh the My Courses list so its per-course progress bar (driven by
      // Moodle's course-level completion %) reflects the newly completed topic
      // when the student navigates back. Without this it stays stale for the
      // query's 5-minute staleTime.
      queryClient.invalidateQueries({
        queryKey: ["studentCourses"],
        exact: false,
      });
    },
  });
};

// Export individual fetch functions for use outside of React Query
export {
  fetchStudentCourses,
  fetchCourseDetails,
  fetchCourseAssignments,
  fetchCourseOverview,
  syncCourses,
};

/**
 * Fetch assignment submission + grade status from Moodle
 * assignId = the Moodle assignment instance ID (details.instance from getModuleDetails)
 */
export const useAssignmentStatusQuery = (
  contextId: string,
  assignId: string | number | undefined,
  token: string | null,
) => {
  return useQuery<AssignmentStatus, Error>({
    queryKey: ["assignmentStatus", contextId, assignId],
    queryFn: async () => {
      const url = getBITSEndApiMap("getAssignmentStatus")
        .replace(":contextId", contextId)
        .replace(":assignId", String(assignId));
      const response = await api.get(url);
      return response.data.data as AssignmentStatus;
    },
    enabled: !!contextId && !!assignId && !!token,
    staleTime: 60 * 1000,
  });
};
